import { 
  useCallback, 
  useContext, 
  useEffect, 
  useLayoutEffect, 
  useMemo, 
  useRef, 
  useState 
} from "react"

import { 
  EmptyPlaceholder, 
  ErrorPlaceholder, 
  LoadingPlaceholder 
} from "@components"

import { NotificationContext } from "@context"

import { 
  getGraphQLErrorMessage, 
  shouldSessionFullSync, 
  shouldSessionPartialUpdate 
} from "@helpers"

import { useTimetable, useUpdateSlotStatus } from "@hooks"
import { Button } from "@heroui/react"
import { PresentationChartBarIcon, SquaresPlusIcon } from "@heroicons/react/24/outline"
import { ChevronRightIcon } from "@heroicons/react/20/solid"
import { useNavigate } from "react-router-dom"

import { 
  Controls, 
  Group, 
  Slot, 
  SlotDetailsModal,
  SlotsHeader 
} from "./Partials"

import PropTypes from "prop-types"

const GROUP_COL_WIDTH = 268 + 32
const OVERSCAN_SLOTS = 0
const FULL_REFRESH_INTERVAL = 585000 // 9 mins 45 secs as not to clash with partial sync
const PARTIAL_REFRESH_INTERVAL = 30000 // 30 seconds

const Timetable = ({ 
  currentSession = null, 
  event, 
  handleRefreshed = () => {},
  isReadOnlyMode, 
  isReady 
}) => {

  /**
   * Context
   */
  const { notify } = useContext(NotificationContext)
  
  /**  
   * States
   */
  const [availabilityChanges, setAvailabilityChanges] = useState({})
  const [isFixed, setIsFixed] = useState(false);
  const [fixedHeaderStyles, setFixedHeaderStyles] = useState({});
  const [mode, setMode] = useState("standard") // standard | availability
  const [page, setPage] = useState(1)
  const [selectedFilters, setSelectedFilters] = useState(new Set())
  const [selectedSlotDetails, setSelectedSlotDetails] = useState(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [slotsPerPage, setSlotsPerPage] = useState(1)

  /**  
   * Refs
   */
  const timetableRef = useRef(null)
  const slotsHeaderRef = useRef(null);
  const lastUpdatedAtRef = useRef(null);

  /**  
   * Hooks
   */
  const navigate = useNavigate()
  
  const { 
    bookedSlotDetailsLoading,
    bookedSlotDetailsError,
    error, 
    fetchBookedSlotDetails,
    fullRefetch, 
    isRefetching, 
    loading, 
    partialRefetch, 
    timetable, 
    updateTimetableAvailabilityCache,
    updateTimetableDeletedBookingCache,
  } = useTimetable(currentSession?.id)
  
  const { updateSlotStatus } = useUpdateSlotStatus()

  /**  
   * Functions
   */
  const getResponsiveClasses = (noOfGroups) => {
    if (noOfGroups <= 5) return "timetable--xs-groups"
    if (noOfGroups <= 8) return "timetable--sm-groups"
    return ""
  }

  const getGroupCounts = (group) => {
    const slots = timetable.groups.find(g => g.id === group.id)?.slots || [];

    return slots.reduce((acc, slot) => {
      acc[slot.status.toLowerCase()] += 1
      return acc;
    }, {absent: 0, attended: 0, available: 0, booked: 0, disabled: 0});
  }

  const getSlotsPerPage = useCallback(() => {
    if (!timetableRef.current) return 1  
    const noOfGroups = timetable?.groups?.length || 0
    const minWidthPerSlot = noOfGroups <= 5 ? 130 : noOfGroups <= 8 ? 80 : 63
    const containerWidth = timetableRef.current.getBoundingClientRect().width - 32 // accounting for padding
    return Math.max(1, Math.floor((containerWidth - GROUP_COL_WIDTH) / minWidthPerSlot))
  }, [timetable])

  const handleSaveAvailabilityChanges = () => {
    const slotUpdates = Object.entries(availabilityChanges).map(([id, status]) => ({id, status}));
    
    updateSlotStatus({ 
      variables: {
        input: {
          session_id: currentSession.id,
          slots: slotUpdates,
          skip_conflicts: true,
        }
      },
      
      onCompleted: (response) => {
        setAvailabilityChanges({})
        
        if (response?.updateSlotsStatus?.skipped?.length > 0) {
          notify({ 
            title: "Skipped Slots", 
            type: "warning", 
            content: `${response?.updateSlotsStatus?.skipped?.length} slot(s) not updated. These slots must have been booked by parents just before saving!`, open: true 
          })
        }
        
        setMode("standard")
      },
      
      update: (cache, { data: { updateSlotsStatus } }) => {
        updateTimetableAvailabilityCache(cache, updateSlotsStatus.updated, updateSlotsStatus.skipped)
      },
      
      onError: (error) => {
        notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(error, 'An error occurred while updating slots. No changes saved!'), open: true })
      }
    })
  }
  
  const handleSlotClick = useCallback((slot, status, isClickable) => {
    if (!isClickable) return;
    
    if (mode === "availability") {
      const newStatus = status === "AVAILABLE" ? "DISABLED" : "AVAILABLE"
      
      setAvailabilityChanges(prev => {
        if (newStatus === slot.status) {
          const { [slot.id]: _, ...rest } = prev;
          return rest;
        }
        
        return { ...prev, [slot.id]: newStatus };
      });
    } else {
      setShowDetailsModal(true)

      fetchBookedSlotDetails(event.id, slot.booking.booking_slot_id).then((details) => {
        setSelectedSlotDetails(details)
      })
    }
  }, [mode, event.id, fetchBookedSlotDetails]);
  
  const noOfSlots = timetable?.groups && timetable.groups.length > 0 ? timetable?.groups[0]?.slots?.length || 0 : 0
  const getTotalPages = useCallback(() => Math.max(1, Math.ceil(noOfSlots / Math.max(1, slotsPerPage))), [noOfSlots, slotsPerPage])
  const totalPages = useMemo(() => getTotalPages(), [getTotalPages])
  
  const handleFirstPage = useCallback(() => setPage(1), [])
  const handleNextPage = useCallback(() => setPage(prevPage => Math.min(prevPage + 1, totalPages)), [totalPages])
  const handlePreviousPage = useCallback(() => setPage(prevPage => Math.max(prevPage - 1, 1)), [])
  const handleLastPage = useCallback(() => setPage(totalPages), [totalPages])
  const handleResize = useCallback(() => setSlotsPerPage(getSlotsPerPage()), [getSlotsPerPage])  
  const handleToggleMode = useCallback(() => setMode(prevMode => prevMode === "standard" ? "availability" : "standard"), [])
  const handleToggleFilter = useCallback((filters) => setSelectedFilters(filters), [])

  const handleManualRefresh = useCallback(() => {
    fullRefetch().then((timetable) => {
      if (timetable?.last_updated_at) {
        lastUpdatedAtRef.current = timetable.last_updated_at
      }
      handleRefreshed()
    })
  }, [fullRefetch, handleRefreshed])

  const isMuted = (slot) => {
    if (mode === "standard" && selectedFilters.size > 0 && !isSlotInFilter(slot)) return true
    if (mode === "availability" && !isSlotClickable(slot)) return true
    return false
  }
  
  const isSlotInFilter = (slot) => {
    if (selectedFilters.has("booked") && ["ABSENT", "ATTENDED", "BOOKED"].includes(slot.status)) return true
    if (selectedFilters.has("unknown") && ["BOOKED"].includes(slot.status)) return true
    if (selectedFilters.has(slot.status.toLowerCase())) return true
  
    return false
  }

  const isSlotClickable = (slot) => {
    if (mode === "availability" && ["AVAILABLE", "DISABLED"].includes(slot.status)) {
      return true;
    }

    if (mode === "standard" && ["ABSENT", "ATTENDED", "BOOKED"].includes(slot.status)) {
      return true;
    }

    return false;
  }

  /** 
   * Effects 
   */

  // reset lastUpdatedAt when session changes
  useEffect(() => {
    lastUpdatedAtRef.current = null
    setPage(1)
    setAvailabilityChanges({})
    setMode("standard")
  }, [currentSession?.id])

  // keep lastUpdatedAt synced with server responses
  useEffect(() => {
    if (timetable?.last_updated_at) {
      lastUpdatedAtRef.current = timetable.last_updated_at;
    }
  }, [timetable?.last_updated_at])

  useEffect(() => {
    const id = setInterval(() => {
      if (lastUpdatedAtRef.current && shouldSessionPartialUpdate(currentSession?.starts_at, currentSession?.ends_at, event?.open_parents, event?.open_staff)) {
        partialRefetch(lastUpdatedAtRef.current).then(timetable => {
          lastUpdatedAtRef.current = timetable.last_updated_at
          handleRefreshed()
        })
      }
    }, PARTIAL_REFRESH_INTERVAL)
    
    return () => clearInterval(id)
  }, [currentSession?.id, currentSession?.starts_at, currentSession?.ends_at, event?.id, event?.open_parents, event?.open_staff, handleRefreshed, partialRefetch]);

  useEffect(() => {
    const id = setInterval(() => {
      if (shouldSessionFullSync(currentSession.ends_at)) {
        fullRefetch().then((timetable) => {
          if (timetable?.last_updated_at) {
            lastUpdatedAtRef.current = timetable.last_updated_at
          }

          handleRefreshed()
        })
      }
    }, FULL_REFRESH_INTERVAL);

    return () => clearInterval(id);
  }, [currentSession?.id, currentSession?.ends_at, fullRefetch, handleRefreshed]);

  useEffect(() => {
    setPage((page) => Math.min(Math.max(1, page), totalPages))
  }, [slotsPerPage, noOfSlots, totalPages])

  useEffect(() => {
    function onScroll() {
      if (!timetableRef.current || !slotsHeaderRef.current) return;

      const rect = timetableRef.current.getBoundingClientRect()
      const headerHeight = slotsHeaderRef.current.offsetHeight

      if (rect.top <= 0 && rect.bottom - 5 > headerHeight) {
        setIsFixed(true);

        setFixedHeaderStyles({
          borderRadius: 0,
          position: "fixed",
          top: "64px",
          left: `calc(${rect.left}px + 1rem)`,
          width: (rect.width - 300) + "px",
          zIndex: 1000,
          background: "#172554",
          overflow: "hidden",
        });
      } else {
        setIsFixed(false);
        setFixedHeaderStyles({});
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  /**  
   * Layout Effects
   */
  useLayoutEffect(() => {
    setSlotsPerPage(getSlotsPerPage())
  }, [timetable, getSlotsPerPage])

  useLayoutEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [handleResize])

  /** 
   * render 
   */   
  const currentPageData = useMemo(() => {
    if (!timetable || !timetable.groups || timetable.groups.length === 0) return []
    
    const lastPageStart = Math.max(0, noOfSlots - slotsPerPage)
    const start = Math.min((page - 1) * slotsPerPage, lastPageStart)
    const end = Math.min(start + slotsPerPage + OVERSCAN_SLOTS, noOfSlots)

    return timetable.groups.map(group => ({...group, slots: group.slots.slice(start, end)}))
  }, [noOfSlots, page, slotsPerPage, timetable])

  const slotsHeader = currentPageData[0]?.slots ?? []
  const responsiveClasses = getResponsiveClasses(timetable?.groups?.length || 0)

  return (
    <div ref={timetableRef} className={`timetable ${responsiveClasses}`}>
      {error && <ErrorPlaceholder classNames="m-3" caption="An error occurred loading the timetable" error={error} />}
      
      {!error && (loading || !timetable || !isReady) && <LoadingPlaceholder classNames="m-3" caption="Please wait loading timetable..." icon={PresentationChartBarIcon} />}

      {!loading && !error && timetable && timetable?.groups && timetable.groups.length === 0 && (
        <EmptyPlaceholder classNames="m-3" icon={SquaresPlusIcon} title="No Groups Found" description="You currently have no lessons for this session. Click below to add lessons.">
          <Button className="font-semibold text-white" color="success" size="lg" onPress={() => navigate(`/event/${event.id}/groups`)}>
            Add Lessons <ChevronRightIcon className="h-6 text-white" />
          </Button>
        </EmptyPlaceholder>
      )}

      {!loading && isReady && !error && timetable && timetable?.groups && timetable.groups.length > 0 && (
        <div className="overflow-hidden">
          <Controls 
            page={page} 
            totalPages={totalPages} 
            handleFirstPage={handleFirstPage}
            handlePreviousPage={handlePreviousPage}
            handleNextPage={handleNextPage}
            handleLastPage={handleLastPage}
            handleRefresh={handleManualRefresh}
            handleToggleFilter={handleToggleFilter}
            handleToggleMode={handleToggleMode}
            isReadOnlyMode={isReadOnlyMode}
            isRefetching={isRefetching}
            mode={mode}
            selectedFilters={selectedFilters}
            noOfAvailabilityChanges={Object.keys(availabilityChanges).length}
            handleCancelAvailabilityChanges={() => setAvailabilityChanges({})}
            handleSaveAvailabilityChanges={handleSaveAvailabilityChanges}
          />
          
          <div className="timetable-wrapper">
            <SlotsHeader ref={slotsHeaderRef} slots={slotsHeader} isFixed={isFixed} style={isFixed ? fixedHeaderStyles : {}} />

            <div className="timetable-slots">
              {currentPageData.map((group) => {
                const counts = getGroupCounts(group)

                return (
                  <div key={group.id} className="flex timetable-row">
                    <Group
                      id={group.id}
                      title={group.title} 
                      available={counts.available} 
                      booked={counts.booked} 
                      disabled={counts.disabled}
                      absent={counts.absent}
                      attended={counts.attended}
                    />

                    <div className="flex timetable-slots__row">
                      {group.slots.map((slot) => {
                        const status = availabilityChanges[slot.id] || slot.status
                        const isHighlighted = (selectedFilters.has("disabled") || mode === "availability") && status === "DISABLED"
                        const isClickable = isSlotClickable(slot)

                        return (
                          <Slot 
                            handleClick={handleSlotClick}
                            key={slot.id} 
                            slot={slot}
                            status={status}
                            isMuted={isMuted(slot)} 
                            isHighlighted={isHighlighted}
                            isClickable={isClickable}
                          />
                        )
                      })}
                    </div>
                  </div>
                )}
              )}
            </div>
          </div>
        </div>
      )}

      {showDetailsModal && (
        <SlotDetailsModal
          details={selectedSlotDetails} 
          error={bookedSlotDetailsError}
          handleClose={() => setShowDetailsModal(false)} 
          loading={bookedSlotDetailsLoading}
          session={currentSession} 
          updateTimetableDeletedBookingCache={updateTimetableDeletedBookingCache}
        />
      )}
    </div>
  )
}

Timetable.propTypes = {
  currentSession: PropTypes.object,
  event: PropTypes.object.isRequired,
  handleRefreshed: PropTypes.func,
  isReadOnlyMode: PropTypes.bool.isRequired,
  isReady: PropTypes.bool.isRequired,
}

export default Timetable